import React from 'react';
import { Box, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { toLabelPayload } from '@trackly/shared';

// Only the 3 fields a label actually renders — lets both the bulk grid selection
// (`AssetListView`) and a single-asset print (`AssetView`, from AssetEdit) satisfy this without
// either caller fabricating unrelated fields just to type-check.
export interface LabelAsset {
  id: string;
  assetTag: string;
  name: string;
}

interface AssetLabelProps {
  asset: LabelAsset;
}

// OL1735LP label geometry (research.md item 8): 1.75" x 0.75" (44.45mm x 19.05mm). The QR is
// bounded by the label's height, not its width — sized to leave a small quiet-zone margin inside
// that 0.75" so it stays reliably scannable at V2/level M (a 36-char TRK: payload). Exported so
// `LabelSheet` sizes its grid cells from the same numbers, rather than a second, drifting copy.
export const LABEL_WIDTH_IN = 1.75;
export const LABEL_HEIGHT_IN = 0.75;
const QR_SIZE_PX = 60; // ~0.625" at 96dpi — leaves ~2mm quiet zone inside the 0.75" label height

/**
 * A single printable asset label (T070): an SVG QR code of the T130 `TRK:` payload, the asset's
 * tag in bold, and its name wrapped/truncated over two lines — sized to the OL1735LP die-cut
 * geometry above. Used both by `LabelSheet` (bulk print) and stand-alone in a single-asset
 * preview.
 */
export const AssetLabel: React.FC<AssetLabelProps> = ({ asset }) => (
  <Box
    className="asset-label"
    sx={{
      width: `${LABEL_WIDTH_IN}in`,
      height: `${LABEL_HEIGHT_IN}in`,
      display: 'flex',
      alignItems: 'center',
      gap: '2mm',
      padding: '1mm',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}
  >
    <QRCodeSVG value={toLabelPayload(asset.id)} size={QR_SIZE_PX} level="M" marginSize={1} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '8pt', lineHeight: 1.1 }} noWrap>
        {asset.assetTag}
      </Typography>
      <Typography
        sx={{
          fontSize: '7pt',
          lineHeight: 1.1,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {asset.name}
      </Typography>
    </Box>
  </Box>
);

export default AssetLabel;
